import React from "react";
import { Sparkles, Code2, Layers, Zap } from "lucide-react";
import { Reveal } from "@/components/site/SiteLayout";

/**
 * ============================================================
 * WORKSHOP HERO SECTION
 * ============================================================
 * Direct, restrained, and technical hero header.
 * Communicates the value proposition of Shiveda's practitioner-led
 * engineering intensives without exaggerated marketing claims.
 */
export default function WorkshopHero({ totalWorkshops = 0, domainCount = 0 }) {
  return (
    <section className="relative pt-6 pb-12 overflow-hidden">
      {/* Background ambient lighting effects */}
      <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-[800px] h-[350px] bg-primary/[0.08] rounded-full blur-3xl -z-10" />

      <div className="max-w-4xl mx-auto text-center space-y-5">
        {/* Badge */}
        <Reveal delay={0}>
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent px-4 py-1.5 text-xs text-white backdrop-blur-xl">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            <span className="font-semibold tracking-wide uppercase text-[11px] text-primary">
              Hands-On Engineering Workshops
            </span>
          </div>
        </Reveal>

        {/* Headline */}
        <Reveal delay={80}>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.1]">
            Learn. Build.{" "}
            <span className="bg-gradient-to-r from-primary via-primary/85 to-white bg-clip-text text-transparent">
              Ship.
            </span>
          </h1>
        </Reveal>

        {/* Supporting Narrative */}
        <Reveal delay={140}>
          <p className="max-w-2xl mx-auto text-base sm:text-lg leading-relaxed text-white/70">
            Practitioner-led engineering workshops designed to help developers and students
            build real-world technical skills. Master generative AI, distributed systems,
            and cloud-native architecture through production-grade codebases.
          </p>
        </Reveal>

        {/* Truthful Metrics Strip (strictly derived from active catalog data) */}
        {totalWorkshops > 0 && (
          <Reveal delay={200}>
            <div className="pt-4 flex flex-wrap items-center justify-center gap-3 sm:gap-6 text-xs text-white/60">
              <div className="flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur-md">
                <Code2 className="w-3.5 h-3.5 text-primary" />
                <span className="text-white font-semibold">{totalWorkshops}</span>
                <span>Workshops</span>
              </div>

              {domainCount > 0 && (
                <div className="flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur-md">
                  <Layers className="w-3.5 h-3.5 text-primary" />
                  <span className="text-white font-semibold">{domainCount}</span>
                  <span>Technical Domains</span>
                </div>
              )}

              <div className="flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur-md">
                <Zap className="w-3.5 h-3.5 text-primary" />
                <span>Hands-on Code Labs</span>
              </div>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
}
